import type { ReactNode } from 'react';

import type { CustomerProfileView, HotelLoyaltyPrograms } from '@voll-intelligence/domain';

import { t } from '@web/i18n';
import { Kv, KvSection, ToneChip } from '@web/ui/core/molecules';
import {
  formatDate,
  formatDateOrNull,
  hotelLoyaltyLines,
  mileagePlanLines,
} from '@web/ui/core/utils';

const ALL_SECTIONS = ['identity', 'travelDocs', 'preferences'] as const;

/**
 * The operational profile as three `KvSection` cards (identity, travel documents,
 * preferences) of stacked key/value pairs. Absent fields render a labelled faint
 * text ("Não informado", "Nenhum", …) rather than a bare dash. Tier-3 documents
 * (CPF/RG/passport) are absent from `CustomerProfileView` by construction (LGPD
 * gate) — nothing here renders them.
 *
 * `hotelLoyalty` is NOT part of `CustomerProfileView`: it comes from the
 * dossiê's own store (hotel-integrations), so each caller reads and passes it
 * separately from `profile`.
 *
 * An `ui/core` organism because two features show it: the customer screen's Perfil
 * tab and the alerts panel's "Dados do viajante". The traveler view shows only
 * `travelDocs` and `preferences` (identity is in its header).
 */
export function CustomerProfilePanel({
  profile,
  hotelLoyalty = null,
  sections = ALL_SECTIONS,
  oneColumn = false,
}: Readonly<{
  profile: CustomerProfileView;
  hotelLoyalty?: HotelLoyaltyPrograms | null;
  /** Which sections to show; they always render in this fixed order. Default: all three. */
  sections?: readonly (typeof ALL_SECTIONS)[number][];
  /** Stack each section's key/value pairs in a single column. */
  oneColumn?: boolean;
}>): React.JSX.Element {
  return (
    <div className="flex flex-col gap-5">
      {sections.includes('identity') ? (
        <IdentitySection profile={profile} oneColumn={oneColumn} />
      ) : null}
      {sections.includes('travelDocs') ? (
        <TravelDocsSection profile={profile} oneColumn={oneColumn} />
      ) : null}
      {sections.includes('preferences') ? (
        <PreferencesSection profile={profile} hotelLoyalty={hotelLoyalty} oneColumn={oneColumn} />
      ) : null}
    </div>
  );
}

function IdentitySection({
  profile,
  oneColumn,
}: Readonly<{ profile: CustomerProfileView; oneColumn: boolean }>): React.JSX.Element {
  return (
    <KvSection icon="user" label={t('customers.profile.section.identity')} oneColumn={oneColumn}>
      <Field label={t('customers.profile.name')} value={profile.name} />
      <Field
        label={t('customers.profile.email')}
        value={
          profile.email ? (
            <a href={`mailto:${profile.email}`} className="text-primary hover:underline">
              {profile.email}
            </a>
          ) : null
        }
      />
      <Field label={t('customers.profile.phone')} value={profile.phone} mono />
      <Field label={t('customers.profile.createdAt')} value={formatDateOrNull(profile.createdAt)} />
      <Field label={t('customers.profile.birthdate')} value={formatDateOrNull(profile.birthdate)} />
      <Field label={t('customers.profile.gender')} value={genderLabel(profile.gender)} />
      <Field label={t('customers.profile.nationality')} value={profile.nationality?.name} />
      <Field label={t('customers.profile.countryCode')} value={profile.countryCode} mono />
      <Field label={t('customers.profile.nameCivil')} value={profile.nameCivil} />
      <Field label={t('customers.profile.preferrerName')} value={profile.preferrerName} />
    </KvSection>
  );
}

function TravelDocsSection({
  profile,
  oneColumn,
}: Readonly<{ profile: CustomerProfileView; oneColumn: boolean }>): React.JSX.Element {
  return (
    <KvSection
      icon="identity"
      label={t('customers.profile.section.travelDocs')}
      oneColumn={oneColumn}
    >
      <Field label={t('customers.profile.boardingName')} value={boardingName(profile)} />
      <Field label={t('customers.profile.tsaPreCheck')} value={profile.tsaPreCheck} />
      <Field
        label={t('customers.profile.visas')}
        value={lines(visaLines(profile))}
        emptyLabel={t('customers.empty.none')}
      />
      <Field
        label={t('customers.profile.vaccines')}
        value={lines(vaccineLines(profile))}
        emptyLabel={t('customers.empty.noneFem')}
      />
      <Field
        label={t('customers.profile.emergencyContacts')}
        value={lines(emergencyContactLines(profile))}
        emptyLabel={t('customers.empty.noEmergencyContacts')}
        wide
      />
    </KvSection>
  );
}

function PreferencesSection({
  profile,
  hotelLoyalty,
  oneColumn,
}: Readonly<{
  profile: CustomerProfileView;
  hotelLoyalty: HotelLoyaltyPrograms | null;
  oneColumn: boolean;
}>): React.JSX.Element {
  return (
    <KvSection
      icon="sliders"
      label={t('customers.profile.section.preferences')}
      oneColumn={oneColumn}
    >
      <Field
        label={t('customers.profile.mileagePlans')}
        value={lines(mileagePlanLines(profile))}
        emptyLabel={t('customers.empty.none')}
        mono
      />
      <Field
        label={t('customers.profile.hotelLoyalty')}
        value={lines(hotelLoyaltyLines(hotelLoyalty))}
        emptyLabel={t('customers.empty.none')}
        mono
      />
      <Field
        label={t('customers.profile.costCenterDefault')}
        value={profile.costCenterDefault?.costCenter?.name}
      />
      <Field
        label={t('customers.profile.userCostCenters')}
        value={<UserCostCenters profile={profile} />}
        wide
      />
    </KvSection>
  );
}

/** A label/value cell rendering a labelled faint text when the value is absent. */
function Field({
  label,
  value,
  emptyLabel,
  mono,
  wide,
}: Readonly<{
  label: string;
  value: ReactNode;
  emptyLabel?: string;
  mono?: boolean;
  wide?: boolean;
}>): React.JSX.Element {
  const empty = value == null || value === '';
  return (
    <Kv label={label} mono={mono && !empty} empty={empty} wide={wide}>
      {empty ? (emptyLabel ?? t('customers.empty.notInformed')) : value}
    </Kv>
  );
}

const GENDER_LABELS: Record<string, string> = {
  MALE: 'customers.gender.male',
  FEMALE: 'customers.gender.female',
  OTHER: 'customers.gender.other',
};

/** Maps a known gender enum to its label; passes any other value through. */
function genderLabel(value: string | null | undefined): string | null {
  if (!value) return null;
  const key = GENDER_LABELS[value.toUpperCase()];
  return key ? t(key as Parameters<typeof t>[0]) : value;
}

function lines(items: string[]): ReactNode {
  if (items.length === 0) return null;
  return (
    <span className="flex flex-col gap-0.5">
      {items.map((line, index) => (
        <span key={`${line}-${String(index)}`}>{line}</span>
      ))}
    </span>
  );
}

function UserCostCenters({
  profile,
}: Readonly<{ profile: CustomerProfileView }>): React.JSX.Element {
  if (profile.userCostCenters?.allActive) {
    return <>{t('customers.profile.userCostCenters.all')}</>;
  }
  const names = (profile.userCostCenters?.costCenters ?? [])
    .map((entry) => entry.costCenter?.name ?? entry.company?.name ?? '')
    .filter(Boolean);
  if (names.length === 0) return <>{t('customers.empty.none')}</>;
  return (
    <span className="flex flex-wrap gap-1.5">
      {names.map((name, index) => (
        <ToneChip key={`${name}-${String(index)}`} tone="gray">
          {name}
        </ToneChip>
      ))}
    </span>
  );
}

function boardingName(profile: CustomerProfileView): string {
  if (!profile.boardingName) return '';
  return [profile.boardingName.firstName, profile.boardingName.lastName].filter(Boolean).join(' ');
}

function visaLines(profile: CustomerProfileView): string[] {
  return (profile.visas ?? []).map((visa) => {
    const head = [visa.country, visa.code].filter(Boolean).join(' · ') || '—';
    return visa.validUntil ? `${head} (${formatDate(visa.validUntil)})` : head;
  });
}

function vaccineLines(profile: CustomerProfileView): string[] {
  return (profile.vaccines ?? []).map((vaccine) => {
    const head = vaccine.name || '—';
    return vaccine.date ? `${head} (${formatDate(vaccine.date)})` : head;
  });
}

function emergencyContactLines(profile: CustomerProfileView): string[] {
  return (profile.emergencyContacts ?? []).map(
    (contact) => [contact.name, contact.phone].filter(Boolean).join(' · ') || '—',
  );
}
